// src/services/pdf/GPSummaryPDFGenerator.ts
// Orchestrator that composes all sections into a complete PDF document.
// Follows the Single Responsibility Principle — this class only coordinates rendering.

import { PDFDocument } from "./core/PDFDocument";
import { PDF_COLORS } from "./core/colors";
import { PAGE_LAYOUT } from "./core/layout";
import { HeaderSection } from "./sections/HeaderSection";
import { FlagStripSection } from "./sections/FlagStripSection";
import { ClinicalSummarySection } from "./sections/ClinicalSummarySection";
import { GPActionsSection } from "./sections/GPActionsSection";
import { PatientNotesSection } from "./sections/PatientNotesSection";
import type { SectionRenderer } from "./sections/SectionRenderer";
import type { PDFRenderData } from "./types";

export class GPSummaryPDFGenerator {
  private sections: SectionRenderer[];

  constructor() {
    this.sections = [
      new HeaderSection(),
      new FlagStripSection(),
      new ClinicalSummarySection(),
      new GPActionsSection(),
      new PatientNotesSection(),
    ];
  }

  /**
   * Generate the full GP Summary PDF from pre-computed render data.
   * Returns the PDFDocument for flexible output (save, blob, data URI).
   */
  generate(data: PDFRenderData): PDFDocument {
    const doc = new PDFDocument();

    // Render each section in order
    for (const section of this.sections) {
      section.render(doc, data);
    }

    // Footer on each page — disclaimer
    this.renderFooter(doc);

    return doc;
  }

  /**
   * Generate and immediately trigger a browser download.
   */
  generateAndDownload(data: PDFRenderData, filename?: string): void {
    const doc = this.generate(data);
    const name = filename ?? this.buildFilename(data);
    doc.save(name);
  }

  private buildFilename(data: PDFRenderData): string {
    const patientName = data.summaryData.userInfo.fullName
      .replace(/[^a-zA-Z0-9]/g, "_")
      .substring(0, 30);
    const date = data.generatedAt.toISOString().split("T")[0];
    return `GP_Summary_${patientName}_${date}.pdf`;
  }

  private renderFooter(doc: PDFDocument): void {
    const footerY = PAGE_LAYOUT.pageHeight - PAGE_LAYOUT.marginBottom + 5;

    doc.drawLine(
      doc.contentLeft,
      footerY - 3,
      doc.contentRight,
      PDF_COLORS.border
    );

    doc.y = footerY;
    doc.setFont("italic", PAGE_LAYOUT.fontSize.tiny);
    doc.setColor(PDF_COLORS.textMuted);
    doc.text(
      "This summary supports your conversation with your GP. It does not replace professional medical advice. Generated by BodyOwn.",
      doc.contentLeft,
      { maxWidth: doc.contentWidth }
    );
  }
}
